import { useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { validateLoginForm } from '../../utils/validation';
import api from '../../services/api';
import LineLoginButton from './LineLoginButton';
import './LoginForm.css';

interface LoginFormData {
  email: string;
  password: string;
}

interface LineAuthData {
  token: string;
  member: {
    id: string;
    email: string;
    name: string;
    storeName: string | null;
    phone: string | null;
    role: string;
    status: string;
    lastLoginAt: string | null;
    createdAt: string;
    updatedAt: string;
  };
  isNewAccount: boolean;
}

const LoginForm = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);

  // Check if this is a callback URL
  const isCallbackUrl = useMemo(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    return !!(code && state);
  }, [searchParams]);

  // Stable success callback
  const handleSuccess = useCallback(async (lineResult: any) => {
    console.log('[LoginForm] handleSuccess called with:', lineResult);
    try {
      // 調用 weiwei-car-business API 建立/更新用戶
      console.log('[LoginForm] Calling API /api/auth/line/token...');
      const response = await api.post<LineAuthData>('/api/auth/line/token', {
        lineUserId: lineResult.lineUserId,
        displayName: lineResult.displayName,
        pictureUrl: lineResult.pictureUrl,
        email: lineResult.email,
      });

      console.log('[LoginForm] API response:', response);

      if (response.success && response.data) {
        const { token, requiresRegistration, lineProfile } = response.data as any;

        // Check if registration is required
        if (requiresRegistration && lineProfile) {
          console.log('[LoginForm] Registration required, LINE profile:', lineProfile);
          setIsProcessingCallback(false);
          setSubmitError('請完成註冊流程。您的 LINE 帳號尚未綁定到會員帳號。');
          // TODO: Show registration form with LINE profile pre-filled
          return;
        }

        if (!token) {
          console.error('[LoginForm] No token in response');
          setSubmitError('無法獲取登入憑證');
          setIsProcessingCallback(false);
          return;
        }

        localStorage.setItem('auth_token', token);
        console.log('[LoginForm] Token saved to localStorage, reloading page to update AuthContext...');

        // Reload page to update AuthContext
        window.location.reload();
      } else {
        console.error('[LoginForm] API failed:', response.error);
        setSubmitError(response.error?.message || 'Authentication failed');
        setIsProcessingCallback(false);
      }
    } catch (err) {
      console.error('[LoginForm] API error:', err);
      setSubmitError(err instanceof Error ? err.message : 'Authentication failed');
      setIsProcessingCallback(false);
    }
  }, []);

  // Stable error callback
  const handleError = useCallback((err: Error) => {
    setSubmitError(err.message);
    setIsProcessingCallback(false);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validation = validateLoginForm(formData.email, formData.password);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        // Redirect to member dashboard or home
        navigate('/member');
      } else {
        setSubmitError(result.error || 'Login failed. Please try again.');
      }
    } catch {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while processing LINE callback
  if (isProcessingCallback) {
    return (
      <div className="login-form" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '1rem'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #06C755',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p>處理 LINE 登入中...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="login-form">
      <div className="login-form__header">
        <h2 className="login-form__title">會員登入</h2>
        <p className="login-form__subtitle">登入以管理您的廣告和許願</p>
      </div>

      <form onSubmit={handleSubmit} className="login-form__form">
        {submitError && (
          <div className="login-form__error" role="alert">
            {submitError}
          </div>
        )}

        <div className="login-form__field">
          <label htmlFor="email" className="login-form__label">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`login-form__input ${
              errors.email ? 'login-form__input--error' : ''
            }`}
            placeholder="your@email.com"
            disabled={isSubmitting}
            autoComplete="email"
          />
          {errors.email && (
            <span className="login-form__field-error">{errors.email}</span>
          )}
        </div>

        <div className="login-form__field">
          <label htmlFor="password" className="login-form__label">
            密碼 *
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            className={`login-form__input ${
              errors.password ? 'login-form__input--error' : ''
            }`}
            placeholder="請輸入密碼"
            disabled={isSubmitting}
            autoComplete="current-password"
          />
          {errors.password && (
            <span className="login-form__field-error">{errors.password}</span>
          )}
          <div className="login-form__forgot-password">
            <a href="/forgot-password" className="login-form__forgot-link">
              忘記密碼？
            </a>
          </div>
        </div>

        <button
          type="submit"
          className="login-form__submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? '登入中...' : '登入'}
        </button>

        <div className="login-form__divider">
          <span className="login-form__divider-text">或</span>
        </div>

        <LineLoginButton
          isInLineClient={false}
          onSuccess={handleSuccess}
          onError={handleError}
          isCallbackUrl={isCallbackUrl}
          onProcessingChange={setIsProcessingCallback}
        />

        <div className="login-form__footer">
          還不是會員？{' '}
          <a href="/register" className="login-form__link">
            立即註冊
          </a>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
